import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import Table from "@/components/ui/Table";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import CountBox from "@/components/ui/CountBox";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { useMemo, useState, useEffect } from "react";
import { TableColumn } from "@/components/ui/Table";
import { formatNumber } from "@/lib/utils";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";
import { useDailyTransportList } from "@/hooks/settlement/se_dailyTransport/useDailyTransport";
import ExcelButton from "@/components/ui/ExcelButton";
import PrintButton from "@/components/ui/PrintButton";
import { getToday } from "@/lib/date";

export default function DailyTransportPage() {
  const today = getToday();

  // 검색 조건 (UI)
  const [company, setCompany] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("custName");
  const [itemSearchType, setItemSearchType] = useState("itemName");
  const [itemSearchValue, setItemSearchValue] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [exactMatch, setExactMatch] = useState(false);

  // 실제 검색에 사용되는 조건
  const [actualParams, setActualParams] = useState({
    startDate: today,
    endDate: today,
    company: "",
    searchType: "custName",
    search: "",
    exactMatch: false,
    itemSearchType: "itemName",
    itemSearch: "",
  });

  const [tableVer, setTableVer] = useState(0);

  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  // API 호출
  const { data: transportData, isLoading } = useDailyTransportList({
    ...actualParams,
    size: 10000,
  });

  // 날짜 변경 시 자동 조회
  useEffect(() => {
    setActualParams(prev => ({
      ...prev,
      startDate,
      endDate,
    }));
    setTableVer(v => v + 1);
  }, [startDate, endDate]);

  // API 데이터
  const allData = useMemo(() => {
    if (!transportData?.content) return [];
    return transportData.content;
  }, [transportData]);

  // Footer 합계
  const footerGrid = useMemo(() => {
    let salesTotal = 0;
    let purchaseTotal = 0;
    let marginTotal = 0;

    allData.forEach(row => {
      salesTotal += row.salesAmt || 0;
      purchaseTotal += row.purchaseAmt || 0;
      marginTotal += row.marginAmt || 0;
    });

    const marginRate = salesTotal > 0 ? (marginTotal / salesTotal) * 100 : 0;

    return [
      [
        { label: "매출액", value: formatNumber(salesTotal) },
        { label: "매입액", value: formatNumber(purchaseTotal) },
        { label: "운송마진", value: formatNumber(marginTotal) },
        { label: "마진비율", value: `${marginRate.toFixed(2)}%` },
      ],
    ];
  }, [allData]);

  // 회사 옵션
  const companyOptions = useMemo(
    () => [
      { label: "전체", value: "" },
      ...companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })),
    ],
    [companyCodeList]
  );

  const searchTypeOptions = [
    { label: "매출처", value: "custName" },
  ];

  const itemSearchTypeOptions = [
    { label: "품목명", value: "itemName" },
  ];

  const handleReset = () => {
    const resetDate = getToday();
    setCompany("");
    setSearchValue("");
    setSearchType("custName");
    setItemSearchType("itemName");
    setItemSearchValue("");
    setStartDate(resetDate);
    setEndDate(resetDate);
    setDateBtnValue("today");
    setExactMatch(false);
    setActualParams({
      startDate: resetDate,
      endDate: resetDate,
      company: "",
      searchType: "custName",
      search: "",
      exactMatch: false,
      itemSearchType: "itemName",
      itemSearch: "",
    });
    setTableVer(v => v + 1);
  };

  const handleSearch = () => {
    setActualParams({
      startDate,
      endDate,
      company,
      searchType,
      search: searchValue,
      exactMatch,
      itemSearchType,
      itemSearch: itemSearchValue,
    });
    setTableVer(v => v + 1);
  };

  // 테이블 컬럼 정의
  const columns: TableColumn[] = useMemo(
    () => [
      { key: "transDate", header: "등록일자", bodyAlign: "center", width: "100px" },
      { key: "custName", header: "매출처", bodyAlign: "left", width: "140px" },
      { key: "itemName", header: "품목", bodyAlign: "left", width: "120px" },
      { key: "loadAgentName", header: "대리점", bodyAlign: "left", width: "100px", group: "상차" },
      { key: "loadQty", header: "수량", bodyAlign: "right", width: "60px", group: "상차", render: (v: number) => v?.toFixed(2) || "" },
      { key: "unloadAgentName", header: "대리점", bodyAlign: "left", width: "100px", group: "하차" },
      { key: "unloadQty", header: "수량", bodyAlign: "right", width: "60px", group: "하차", render: (v: number) => v?.toFixed(2) || "" },
      { key: "vehicleNumber", header: "번호", bodyAlign: "center", width: "100px", group: "하차" },
      { key: "carrierName", header: "운송사", bodyAlign: "left", width: "120px", group: "차량" },
      { key: "carType", header: "형태", bodyAlign: "center", width: "60px", group: "차량" },
      { key: "salesAmt", header: "매출액", bodyAlign: "right", width: "100px", render: (v: number) => formatNumber(v ?? 0) },
      { key: "purchaseAmt", header: "매입액", bodyAlign: "right", width: "100px", render: (v: number) => formatNumber(v ?? 0) },
      { key: "marginAmt", header: "운송마진", bodyAlign: "right", width: "90px", group: "운송마진", render: (v: number) => formatNumber(v ?? 0) },
      { key: "marginRate", header: "비율", bodyAlign: "right", width: "60px", group: "운송마진", render: (v: number) => v ? `${v.toFixed(2)}%` : "" },
      { key: "remark", header: "비고", bodyAlign: "left", width: "200px" },
    ],
    []
  );

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="회사구분"
            className="w-32"
            value={company}
            onChange={setCompany}
            options={companyOptions}
          />
          <div className="mr-3" />
          <DateBox
            label="등록일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />
          <div className="mr-3" />
          <SelectBox
            label="검색구분"
            className="w-24"
            value={searchType}
            onChange={setSearchType}
            options={searchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            placeholder=""
          />
          <div className="mr-3" />
          <SelectBox
            label="검색구분"
            className="w-24"
            value={itemSearchType}
            onChange={setItemSearchType}
            options={itemSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={itemSearchValue}
            onChange={e => setItemSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            placeholder=""
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼/카운트 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PrintButton
              data={allData}
              columns={columns}
              fileName="일일운송명세서"
              title="일일운송명세서"
              skipPermissionCheck
            />
            <ExcelButton
              data={allData}
              columns={columns}
              fileName="일일운송명세서"
              skipPermissionCheck
            />
          </div>
        </div>

        {/* 본문 표 */}
        <div className="flex-1 min-h-0">
          <Table
            key={`daily-transport-table-v${tableVer}`}
            data={allData}
            className="h-full"
            showRowNumber
            resizable
            columns={columns}
            maxHeight="100%"
            loading={isLoading}
            emptyMessage="조회 조건을 입력하고 조회 버튼을 클릭하세요"
          />
        </div>
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
    </div>
  );
}
